import { isHighDPI } from '@trello/browser';
import {
  CanonicalCard,
  CanonicalAvatar as Avatar,
} from '@atlassian/trello-canonical-components';
import { forTemplate } from '@trello/i18n';
import {
  biggestPreview,
  smallestPreviewBiggerThan,
} from '@trello/image-previews';
import type { FunctionComponent } from 'react';
import React from 'react';
import type { CardLabelType } from '@trello/labels';
import type { CardRole } from '@trello/card-roles';
import { formatHumanDate, getStringForCombinedDateBadge } from '@trello/dates';
import { byAttributeCaseInsensitive } from '@trello/arrays';
import { CompactCardLabel, sortLabels } from '@trello/labels';
import type { AvatarSources } from 'app/src/components/MemberAvatar/MemberAvatar.types';
import { CardTemplateBadge } from '@atlassian/trello-canonical-components/src/card-front/Badges';
import {
  Red400,
  TrelloBlue300,
  Green400,
  Orange400,
  Purple400,
  Yellow400,
  Pink400,
  Sky400,
  Lime400,
  N800,
} from '@trello/colors';
import { seesVersionedVariation } from '@trello/feature-flag-client';
import { LinkCard } from 'app/src/components/CardFronts/LinkCard';
import { noop } from 'app/src/noop';
import type { PIIString } from '@trello/privacy';
import { token } from '@trello/theme';

type HasNonNullKeys<T, K extends keyof T> = {
  [key in keyof T]: key extends K ? Exclude<T[key], null | undefined> : T[key];
};

const {
  ArchivedBadge,
  AttachmentsBadge,
  Card,
  CardBadges,
  CardCover,
  CardDetails,
  CardLabels,
  CardMembers,
  CardStickers,
  CardTitle,
  ChecklistBadge,
  CommentsBadge,
  DescriptionBadge,
  DueDateBadge,
  NotificationBadge,
  Sticker,
  SubscribedBadge,
  TrelloAttachmentsBadge,
  VotesBadge,
} = CanonicalCard;

const MAX_COVER_HEIGHT = 260;
const DEFAULT_CARD_WIDTH = 245;
const DEFAULT_STICKER_AREA_HEIGHT = 64;

const format = forTemplate('canonical_card');

export type CoverColor =
  | 'blue'
  | 'green'
  | 'orange'
  | 'purple'
  | 'red'
  | 'yellow'
  | 'pink'
  | 'sky'
  | 'lime'
  | 'black';

const colorCoverColors: {
  [key in CoverColor]: string;
} = {
  red: token('color.background.accent.red.subtle', Red400),
  blue: token('color.background.accent.blue.subtle', TrelloBlue300),
  green: token('color.background.accent.green.subtle', Green400),
  orange: token('color.background.accent.orange.subtle', Orange400),
  purple: token('color.background.accent.purple.subtle', Purple400),
  yellow: token('color.background.accent.yellow.subtle', Yellow400),
  pink: token('color.background.accent.magenta.subtle', Pink400),
  sky: token('color.background.accent.teal.subtle', Sky400),
  lime: token('trello.color.background.accent.lime.subtle', Lime400),
  black: token('color.background.accent.gray.bolder', N800),
};

const colorCoverColorBlindPattern: {
  [key in CoverColor]:
    | 'stripe-diagonal'
    | 'wave-square'
    | 'stripe-vertical'
    | 'dots-checker'
    | 'groove-diagonal'
    | 'stripe-horizontal'
    | 'brick-horizontal'
    | 'brick-diagonal'
    | 'scale'
    | 'brick-vertical';
} = {
  red: 'dots-checker',
  blue: 'stripe-horizontal',
  green: 'stripe-diagonal',
  orange: 'stripe-vertical',
  purple: 'groove-diagonal',
  yellow: 'wave-square',
  pink: 'brick-horizontal',
  sky: 'brick-diagonal',
  lime: 'scale',
  black: 'brick-vertical',
};

export interface CoverPhoto {
  color?: CoverColor | null;
  edgeColor?: string | null;
  previews?: {
    scaled: boolean;
    height: number;
    url: string;
    width: number;
  }[];
  size?: 'normal' | 'full';
  sharedSourceUrl?: string;
  url?: string;
}

/*
 * Extract the height, img url, and background color/size for the card cover
 * from the CoverPhoto. Calculates the height dynamically based on the
 * cover dimensions and available space (which is not *actually* the available
 * space, but based on some given defaults). Has an escape hatch to override
 * the height with a fixed size for use-cases where that's needed - such as in
 * search results.
 */
const getCoverProps = (
  cover: CoverPhoto | null | undefined,
  {
    hasStickers,
    heightOverride,
    colorBlind,
  }: {
    hasStickers?: boolean;
    heightOverride?: number | undefined;
    colorBlind?: boolean;
  } = {},
): {
  height: number;
  img?: string;
  bgColor?: string | null;
  bgSize?: 'cover' | 'contain';
  sharedSourceUrl?: string;
} => {
  const details = {
    height: 0,
    img: '' as string | undefined,
    bgColor: '' as string | undefined | null,
    bgSize: 'cover' as 'cover' | 'contain' | undefined,
    sharedSourceUrl: '' as string | undefined,
    pattern: null as string | null,
  };

  if (!cover) {
    return details;
  }

  const preview = cover
    ? smallestPreviewBiggerThan(cover.previews, isHighDPI() ? 600 : 300) ||
      biggestPreview(cover.previews)
    : null;

  if (preview) {
    details.img = preview.url ? preview.url : cover.url;
    details.sharedSourceUrl = cover.sharedSourceUrl;
    details.bgColor = cover.edgeColor;

    const max = Math.min(preview.height, MAX_COVER_HEIGHT);
    const scaled = (preview.height * DEFAULT_CARD_WIDTH) / preview.width;
    if (scaled <= max) {
      details.height = scaled;
      details.bgSize = 'cover';
    } else {
      details.height = max;
      details.bgSize = 'contain' as 'cover' | 'contain';
    }
  }

  if (cover.color) {
    details.bgColor = colorCoverColors[cover.color as CoverColor];
    details.height = hasStickers ? 64 : 32;

    if (colorBlind) {
      details.bgSize = undefined;
      details.pattern = colorCoverColorBlindPattern[cover.color as CoverColor];
    }
  }

  // Let above run based on default sizing and *then* override
  details.height = heightOverride ? heightOverride : details.height;

  return details;
};

export interface CardBadges {
  attachments: number;
  attachmentsByType: {
    trello: {
      board: number;
      card: number;
    };
  };
  checkItems: number;
  checkItemsChecked: number;
  checkItemsEarliestDue?: Date | null;
  comments: number;
  description: boolean;
  due?: Date | null;
  dueComplete: boolean;
  start?: Date | null;
  subscribed: boolean;
  viewingMemberVoted: boolean;
  votes: number;
}

export interface CardMember {
  activityBlocked?: boolean;
  avatarSource?: AvatarSources;
  avatars?: { [size: string]: string } | null;
  fullName?: PIIString;
  id: string;
  initials?: PIIString;
}

export interface CardSticker {
  id: string;
  image: string;
  imageUrl: string;
  imageScaled: string | null;
  left: number;
  top: number;
  rotate: number;
  zIndex: number;
}

interface CardContentsProps {
  readonly name?: string;
  readonly badges?: CardBadges;
  readonly team?: {
    memberships: {
      deactivated: boolean;
      idMember: string;
    }[];
  };
  readonly members?: CardMember[];
  readonly colorBlind?: boolean;
  readonly labels?: CardLabelType[];
  readonly cover?: CoverPhoto | null;
  readonly coverHeight?: number;
  readonly archived?: boolean;
  readonly isTemplate?: boolean;
  readonly stickers?: CardSticker[];
  readonly numNotifications?: number;
  readonly expandLabels?: boolean;
  readonly replacements?: {
    CardTitle?: React.ReactElement;
  };
  readonly truncateNameForFullCover?: boolean;
  readonly cardRole?: CardRole | null;
}

export const CardContents: FunctionComponent<CardContentsProps> = ({
  name,
  team,
  members = [],
  labels = [],
  colorBlind,
  cover,
  coverHeight,
  archived,
  badges,
  isTemplate,
  stickers = [],
  expandLabels,
  numNotifications,
  replacements = {},
  cardRole,
}) => {
  const trelloAttachments =
    badges && badges.attachmentsByType
      ? badges.attachmentsByType.trello.board +
        badges.attachmentsByType.trello.card
      : 0;
  const attachments = badges ? badges.attachments - trelloAttachments : 0;
  const coverProps = getCoverProps(cover, {
    hasStickers: stickers.length > 0,
    heightOverride: coverHeight,
    colorBlind,
  });
  const deactivatedMembers = new Set(
    team && team.memberships
      ? team.memberships
          .filter((membership) => membership.deactivated)
          .map((membership) => membership.idMember)
      : [],
  );

  const shouldSeeCombinedBadges = seesVersionedVariation(
    'ecosystem.timeline-version',
    'stable',
  );

  if (cardRole === 'link' && name) {
    return (
      <CardDetails>
        <LinkCard
          url={name}
          isEditable={false}
          openEditor={noop}
          hideCover={true}
          hideActions={true}
        />
      </CardDetails>
    );
  }

  return (
    <>
      {cover && cover.size !== 'full' && <CardCover {...coverProps} />}
      {stickers.length > 0 && (
        <CardStickers
          height={
            coverProps.height ? coverProps.height : DEFAULT_STICKER_AREA_HEIGHT
          }
        >
          {stickers.map((sticker) => (
            <Sticker
              key={sticker.id}
              left={sticker.left}
              top={sticker.top}
              alt={sticker.image}
              img={
                isHighDPI() && sticker.imageScaled
                  ? sticker.imageScaled
                  : sticker.imageUrl
              }
              rotate={sticker.rotate}
              zIndex={sticker.zIndex}
            />
          ))}
        </CardStickers>
      )}
      <CardDetails>
        {labels && labels.length > 0 && (
          <CardLabels>
            {sortLabels(labels)
              .filter((label) => !!label.color)
              // @ts-expect-error
              .map((label: HasNonNullKeys<CardLabelType, 'color'>) => (
                <CompactCardLabel key={label.id} label={label} tabIndex={-1}>
                  {expandLabels ? label.name || ' ' : null}
                </CompactCardLabel>
              ))}
          </CardLabels>
        )}
        {replacements.CardTitle ? (
          replacements.CardTitle
        ) : (
          <CardTitle>{name}</CardTitle>
        )}
        <CardBadges>
          {isTemplate && (
            <CardTemplateBadge>
              {format('card template badge label')}
            </CardTemplateBadge>
          )}
          {!!numNotifications && (
            <NotificationBadge numUnread={numNotifications} />
          )}
          {badges && badges.subscribed && <SubscribedBadge />}
          {badges && badges.votes > 0 && !isTemplate && (
            <VotesBadge
              numVotes={badges.votes}
              voted={badges.viewingMemberVoted}
            />
          )}
          {badges && (badges.start || badges.due) && !isTemplate && (
            <DueDateBadge dueDate={badges.due} isComplete={badges.dueComplete}>
              {shouldSeeCombinedBadges &&
                getStringForCombinedDateBadge(badges.start, badges.due)}
              {!shouldSeeCombinedBadges &&
                badges.due &&
                formatHumanDate(badges.due)}
            </DueDateBadge>
          )}
          {badges && badges.description && <DescriptionBadge />}
          {badges && badges.comments > 0 && (
            <CommentsBadge numComments={badges.comments} />
          )}
          {attachments > 0 && <AttachmentsBadge numAttachments={attachments} />}
          {trelloAttachments > 0 && (
            <TrelloAttachmentsBadge numAttachments={trelloAttachments} />
          )}
          {badges && badges.checkItems > 0 && (
            <ChecklistBadge
              numItems={badges.checkItems}
              numComplete={badges.checkItemsChecked}
              checkItemsEarliestDue={badges.checkItemsEarliestDue}
            />
          )}
          {archived && !isTemplate && (
            <ArchivedBadge>{format('archived card badge label')}</ArchivedBadge>
          )}
        </CardBadges>
        {members && members.length > 0 && (
          <CardMembers>
            {members
              .sort(byAttributeCaseInsensitive('fullName'))
              .reduceRight((result: JSX.Element[], member) => {
                result.push(
                  <Avatar
                    key={member.id}
                    avatarSource={member.avatarSource}
                    deactivated={
                      deactivatedMembers.has(member.id) ||
                      member.activityBlocked
                    }
                    img={member.avatars && member.avatars[30]}
                    initials={member.initials}
                    lightBackground
                  />,
                );

                return result;
              }, [])}
          </CardMembers>
        )}
      </CardDetails>
    </>
  );
};

export interface CardFrontProps extends CardContentsProps {
  className?: string;
  style?: React.CSSProperties;
}

export const CardFront = ({ className, style, ...props }: CardFrontProps) => (
  <Card
    className={className}
    style={style}
    cover={props.cover}
    colorBlind={props.colorBlind}
    coverHeight={props.coverHeight}
    truncateNameForFullCover={props.truncateNameForFullCover}
    hasStickers={!!props.stickers?.length}
  >
    <CardContents {...props} />
  </Card>
);
